import React, { useState, useEffect, useMemo } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { FaBoxOpen, FaFilter, FaEye } from "react-icons/fa";
import { Link } from "react-router-dom";
import axios from "axios";
import { baseurl, imgurl } from "../../baseurl";

function CancelledOrder() {
  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    dateFrom: null,
    dateTo: null,
  });
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const vendorId = localStorage.getItem("vendorId");

  // 🟢 Fetch orders for vendor
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const res = await axios.get(
          `${baseurl}getOrdersByVendorIdByStatus/${vendorId}?status=Cancelled`
        ); // fetch only pending orders from backend if supported
        if (res.data.success) {
          setOrders(res.data.orders || []);
        } else {
          setOrders([]);
        }
      } catch (err) {
        console.error("Error fetching vendor orders:", err);
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };
    if (vendorId) fetchOrders();
  }, [vendorId]);

  // 🟢 Filter logic (search + date)
  const filteredOrders = useMemo(() => {
    return orders.filter((o) => {
      const matchesSearch =
        o.order_uuid.toLowerCase().includes(filters.search.toLowerCase()) ||
        (o.customer?.name || "")
          .toLowerCase()
          .includes(filters.search.toLowerCase());

      const orderDate = new Date(o.created_at);
      const matchesDate =
        (!filters.dateFrom || orderDate >= new Date(filters.dateFrom)) &&
        (!filters.dateTo || orderDate <= new Date(filters.dateTo));

      return matchesSearch && matchesDate;
    });
  }, [orders, filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  const handleDateChange = (name, date) => {
    setFilters({ ...filters, [name]: date[0] });
  };

  return (
    <>
      <Topbar />
      <Sidebar />

      <main className="main-wrapper">
        <div className="main-content">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="breadcrumb-title d-flex align-items-center gap-2">
              <FaBoxOpen /> Cancelled Orders
            </h4>
            <button
              className="btn btn-primary d-flex align-items-center gap-2"
              onClick={() => setShowFilter(true)}
            >
              <FaFilter /> Filters
            </button>
          </div>

          {/* Orders Table */}
          <div className="card shadow-sm border-0">
            <div className="card-body table-responsive">
              {loading ? (
                <div className="text-center py-4 text-muted">
                  Loading orders...
                </div>
              ) : (
                <table className="table table-hover table-striped align-middle">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Order ID</th>
                      <th>Customer</th>
                      <th>Items</th>
                      <th>Total</th>
                      <th>Payment</th>
                      <th>Status</th>
                      <th>Reason</th> 
                      <th>Date</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.length > 0 ? (
                      filteredOrders.map((o, i) => (
                        <tr key={o.order_uuid}>
                          <td>{i + 1}</td>
                          <td className="fw-bold">{o.order_uuid}</td>
                          <td>
                            <div>
                              <div className="fw-semibold">{o.customer?.name}</div>
                              <small className="text-muted">{o.customer?.phone}</small>
                            </div>
                          </td>

                          {/* Product images */}
                          <td>
                            <div className="d-flex align-items-center gap-1 flex-wrap">
                              {o.items?.slice(0, 3).map((item, idx) => (
                                <img
                                  key={idx}
                                  src={
                                    item.product_image
                                      ? `${imgurl}${item.product_image}`
                                      : "/noimage.png"
                                  }
                                  alt={item.product_name}
                                  className="rounded shadow-sm border"
                                  style={{
                                    width: "38px",
                                    height: "38px",
                                    objectFit: "cover",
                                  }}
                                  title={`${item.product_name} ×${item.quantity}`}
                                />
                              ))}
                              {o.items?.length > 3 && (
                                <span className="small text-muted ms-1">
                                  +{o.items.length - 3} more
                                </span>
                              )}
                            </div>
                          </td>

                          <td>₹{o.vendor_total}</td>

                          <td>
                            <span
                              className={`badge ${
                                o.payment_status === "Paid" ? "bg-success" : "bg-secondary"
                              }`}
                            >
                              {o.payment_status}
                            </span>
                            <div>
                              <small className="text-muted">{o.payment_method}</small>
                            </div>
                          </td>

                          <td>
                            <span className="badge bg-warning text-dark">{o.order_status}</span>
                          </td>

                          <td>
  {o.cancellation_reason && (
    <>
      <span className="fw-semibold text-danger">{o.cancellation_reason}</span>
      <br />
    </>
  )}
  
  {o.cancellation_comment && (
    <>
      <small className="text-muted">{o.cancellation_comment}</small>
      <br />
    </>
  )}

  {/* 🖼️ Open image in a new tab */}
  {o.cancellation_image && (
    <>
      <a 
        href={`${imgurl}/${o.cancellation_image}`} 
        target="_blank" 
        rel="noopener noreferrer"
        title="Click to view full image"
      >
        <img 
          src={`${imgurl}/${o.cancellation_image}`} 
          className="img-fluid rounded border hover-shadow" 
          style={{ width: "70px", height: "auto", cursor: "pointer" }} 
          alt="Cancellation proof" 
        />
      </a>
      <br />
    </>
  )}

  {/* 🎥 Open video in a new tab (or watch inline) */}
  {o.cancellation_video && (
    <div className="mt-1 d-flex flex-column align-items-start">
      <video 
        src={`${imgurl}/${o.cancellation_video}`} 
        controls 
        className="rounded border"
        style={{ width: "150px", maxHeight: "100px" }}
      >
        Your browser does not support the video tag.
      </video>
      <a 
        href={`${imgurl}/${o.cancellation_video}`} 
        target="_blank" 
        rel="noopener noreferrer"
        className="btn btn-sm btn-link p-0 mt-1 text-decoration-none"
        style={{ fontSize: "12px" }}
      >
        ↗ Open video in new tab
      </a>
    </div>
  )}
</td>

                          <td>
                            {new Date(o.created_at).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })}
                          </td>

                          <td>
                            <Link
                              to={`/vendor/order-details/${o.order_uuid}`}
                              className="btn btn-sm btn-outline-primary"
                            >
                              <FaEye />
                            </Link>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="9" className="text-center text-muted py-4">
                          No pending orders found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* FILTER PANEL */}
          <div className={`filter-panel ${showFilter ? "open" : ""}`}>
            <div className="filter-header">
              <h5>Filters</h5>
              <button
                className="btn-close"
                onClick={() => setShowFilter(false)}
              ></button>
            </div>

            <div className="filter-body">
              <div className="mb-3">
                <label>Search</label>
                <input
                  type="text"
                  className="form-control"
                  name="search"
                  placeholder="Order ID or Customer"
                  value={filters.search}
                  onChange={handleFilterChange}
                />
              </div>

              <div className="mb-3">
                <label>Date From</label>
                <Flatpickr
                  value={filters.dateFrom}
                  onChange={(date) => handleDateChange("dateFrom", date)}
                  options={{ dateFormat: "Y-m-d" }}
                  className="form-control"
                />
              </div>

              <div className="mb-3">
                <label>Date To</label>
                <Flatpickr
                  value={filters.dateTo}
                  onChange={(date) => handleDateChange("dateTo", date)}
                  options={{ dateFormat: "Y-m-d" }}
                  className="form-control"
                />
              </div>

              <button
                className="btn btn-primary w-100"
                onClick={() => setShowFilter(false)}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      </main>

      <style jsx>{`
        .filter-panel {
          position: fixed;
          right: -400px;
          top: 0;
          width: 350px;
          height: 100%;
          background: #fff;
          box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
          transition: right 0.3s ease;
          z-index: 1050;
          padding: 20px;
        }
        .filter-panel.open {
          right: 0;
        }
        .filter-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid #ddd;
          margin-bottom: 15px;
        }
      `}</style>
    </>
  );
}

export default CancelledOrder;
